import React, { useState, useEffect, useCallback } from 'react';
import { getAllUsers, updateUser, adminCreateUser, deleteUser } from '../services/api';
import { UserPlus, Loader2, X, AlertCircle, Edit2, CheckCircle, Trash2 } from 'lucide-react';
import ProductMultiSelect from '../components/ProductMultiSelect';
import { useTranslation } from 'react-i18next';

const Users = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'confirmation',
        status: 'active',
        allowedProducts: [],
        paymentType: 'FIXED_SALARY',
        monthlySalary: 0,
        commissionPerDeliveredOrder: 0
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showModal]);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getAllUsers();
            if (res.success) {
                setUsers(res.data);
            }
        } catch (err) {
            setError(t('users.messages.fetch_failed'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const showToast = (type, message) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            role: 'confirmation',
            status: 'active',
            allowedProducts: [],
            paymentType: 'FIXED_SALARY',
            monthlySalary: 0,
            commissionPerDeliveredOrder: 0
        });
        setEditingUser(null);
        setError(null);
    };

    const handleOpenCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    const handleOpenEditModal = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            password: '', // Don't show password
            role: user.role,
            status: user.status,
            allowedProducts: user.allowedProducts || [],
            paymentType: user.paymentType || 'FIXED_SALARY',
            monthlySalary: user.monthlySalary || 0,
            commissionPerDeliveredOrder: user.commissionPerDeliveredOrder || 0
        });
        setShowModal(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        try {
            let res;
            if (editingUser) {
                const updateData = { ...formData };
                if (!updateData.password) delete updateData.password;
                res = await updateUser(editingUser._id, updateData);
                if (res.success) {
                    showToast('success', t('users.messages.update_success'));
                    fetchUsers();
                    setShowModal(false);
                }
            } else {
                res = await adminCreateUser(formData);
                if (res.success) {
                    showToast('success', t('users.messages.create_success'));
                    fetchUsers();
                    setShowModal(false);
                }
            }
        } catch (err) {
            const msg = err.response?.data?.message || t('users.messages.operation_failed');
            setError(msg);
            showToast('error', msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAttemptDelete = (user) => {
        if (user.hasOrders) {
            showToast('error', t('users.messages.has_orders'));
            return;
        }
        setUserToDelete(user);
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        setIsSubmitting(true);
        try {
            const res = await deleteUser(userToDelete._id);
            if (res.success) {
                showToast('success', t('users.messages.delete_success'));
                fetchUsers();
                setUserToDelete(null);
            }
        } catch (err) {
            const msg = err.response?.data?.message || t('users.messages.delete_failed');
            showToast('error', msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="px-4 py-6 md:p-8 w-full max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">{t('users.title')}</h1>
                    <p className="mt-1 text-xs md:text-sm text-gray-400 font-medium">{t('users.subtitle')}</p>
                </div>
                <button
                    onClick={handleOpenCreateModal}
                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-gray-900 hover:bg-indigo-600 text-white px-5 py-3 md:py-2.5 rounded-xl shadow-sm transition-all duration-300 font-bold text-[11px] md:text-xs uppercase tracking-wider"
                >
                    <UserPlus size={16} />
                    {t('users.actions.create')}
                </button>
            </div>

            {error && !showModal && (
                <div className="mb-5 bg-red-50 border border-red-100 p-3.5 text-red-600 flex items-center gap-3 rounded-xl text-sm font-medium">
                    <AlertCircle size={18} />
                    <p>{error}</p>
                </div>
            )}

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                        <p className="mt-3 text-gray-400 font-medium text-sm">{t('users.table.loading')}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="px-5 py-3.5 font-bold text-gray-400 uppercase text-[10px] tracking-widest">{t('users.table.user')}</th>
                                    <th className="px-5 py-3.5 font-bold text-gray-400 uppercase text-[10px] tracking-widest">{t('users.table.email')}</th>
                                    <th className="px-5 py-3.5 font-bold text-gray-400 uppercase text-[10px] tracking-widest">{t('users.table.role')}</th>
                                    <th className="px-5 py-3.5 font-bold text-gray-400 uppercase text-[10px] tracking-widest">{t('users.table.status')}</th>
                                    <th className="px-5 py-3.5 font-bold text-gray-400 uppercase text-[10px] tracking-widest">{t('users.table.products')}</th>
                                    <th className="px-5 py-3.5 font-bold text-gray-400 uppercase text-[10px] tracking-widest">{t('users.table.presence')}</th>
                                    <th className="px-5 py-3.5 font-bold text-gray-400 uppercase text-[10px] tracking-widest text-right">{t('users.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {users.map((user) => (
                                    <tr key={user._id} className="hover:bg-indigo-50/30 transition-colors duration-200 group">
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold uppercase ${user.role === 'admin'
                                                    ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white'
                                                    : user.role === 'gerant'
                                                        ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
                                                        : 'bg-indigo-50 text-indigo-600 border border-indigo-100'
                                                    }`}>
                                                    {user.name.charAt(0)}
                                                </div>
                                                <span className="font-semibold text-gray-900 text-sm">{user.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3.5 text-gray-500 font-medium text-sm">{user.email}</td>
                                        <td className="px-5 py-3.5">
                                            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest ${user.role === 'admin'
                                                ? 'bg-purple-50 text-purple-600 border border-purple-100'
                                                : user.role === 'gerant'
                                                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                                    : 'bg-blue-50 text-blue-600 border border-blue-100'
                                                }`}>
                                                {t(`users.roles.${user.role}`)}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <span className={`inline-flex items-center gap-1.5 font-bold text-[10px] uppercase tracking-widest ${user.status === 'active' ? 'text-green-600' : 'text-red-500'
                                                }`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                                                    }`} />
                                                {t(`users.status.${user.status}`)}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            {user.role === "confirmation" ? (
                                                <div className="flex flex-wrap gap-1 max-w-[200px]">
                                                    {user.allowedProducts?.slice(0, 3).map((p, i) => (
                                                        <span key={i} className="text-[9px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-md border border-indigo-100 font-bold truncate max-w-[80px]">
                                                            {typeof p === 'object' ? p.name : p}
                                                        </span>
                                                    ))}
                                                    {(user.allowedProducts?.length > 3) && (
                                                        <span className="text-[9px] bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded-md border border-gray-100 font-bold">
                                                            +{user.allowedProducts.length - 3}
                                                        </span>
                                                    )}
                                                    {(!user.allowedProducts || user.allowedProducts.length === 0) && (
                                                        <span className="text-[10px] text-gray-400 italic font-medium">{t('users.table.none')}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-[10px] font-bold text-gray-400 italic uppercase tracking-widest">{t('users.table.all_access')}</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <div className="flex flex-col gap-0.5">
                                                <span className={`inline-flex items-center gap-1.5 font-bold text-[10px] uppercase tracking-widest ${user.isOnline ? 'text-green-600' : 'text-gray-400'}`}>
                                                    <div className={`w-1.5 h-1.5 rounded-full ${user.isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                                                    {user.isOnline ? t('users.presence.online') : t('users.presence.offline')}
                                                </span>
                                                <span className="text-[10px] text-gray-400 font-medium">
                                                    {user.isOnline
                                                        ? t('users.presence.active_now')
                                                        : user.lastSeenAt
                                                            ? t('users.presence.last_seen', {
                                                                time: new Date(user.lastSeenAt).toLocaleString([], {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })
                                                            })
                                                            : t('common.not_applicable')
                                                    }
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3.5 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenEditModal(user)}
                                                    className="p-2 text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleAttemptDelete(user)}
                                                    className="p-2 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl border border-gray-100">
                        <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                        <p className="mt-3 text-gray-400 font-medium text-xs">{t('users.table.loading')}</p>
                    </div>
                ) : (
                    users.map((user) => (
                        <div key={user._id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm space-y-4 relative group">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold uppercase shrink-0 ${user.role === 'admin'
                                    ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white'
                                    : user.role === 'gerant'
                                        ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
                                        : 'bg-indigo-50 text-indigo-600 border border-indigo-100'
                                    }`}>
                                    {user.name.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-gray-900 text-sm truncate">{user.name}</p>
                                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                                </div>
                                <div className="ml-auto flex items-center gap-1">
                                    <button
                                        onClick={() => handleOpenEditModal(user)}
                                        className="p-2 text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all shrink-0"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleAttemptDelete(user)}
                                        className="p-2 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all shrink-0"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 pt-1">
                                <div>
                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5">{t('users.table.role')}</p>
                                    <span className={`px-2 py-0.5 rounded-lg text-[9px] font-bold uppercase tracking-widest border inline-block ${user.role === 'admin'
                                        ? 'bg-purple-50 text-purple-600 border-purple-100'
                                        : user.role === 'gerant'
                                            ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                            : 'bg-blue-50 text-blue-600 border-blue-100'
                                        }`}>
                                        {t(`users.roles.${user.role}`)}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5">{t('users.table.status')}</p>
                                    <span className={`inline-flex items-center gap-1 font-bold text-[9px] uppercase tracking-widest ${user.status === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                                        <div className={`w-1 h-1 rounded-full ${user.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`} />
                                        {t(`users.status.${user.status}`)}
                                    </span>
                                </div>
                            </div>

                            <div className="pt-3 border-t border-gray-50 grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5">{t('users.table.products')}</p>
                                    {user.role === "confirmation" ? (
                                        <div className="flex flex-wrap gap-1">
                                            {user.allowedProducts?.length > 0 ? (
                                                <>
                                                    {user.allowedProducts.slice(0, 2).map((p, i) => (
                                                        <span key={i} className="text-[8px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-md border border-indigo-100 font-bold truncate max-w-[60px]">
                                                            {typeof p === 'object' ? p.name : p}
                                                        </span>
                                                    ))}
                                                    {user.allowedProducts.length > 2 && (
                                                        <span className="text-[8px] bg-gray-50 text-gray-500 px-1 py-0.5 rounded-md border border-gray-100 font-bold">
                                                            +{user.allowedProducts.length - 2}
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-[9px] text-gray-400 italic font-medium">{t('users.table.none')}</span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('users.table.all_access')}</span>
                                    )}
                                </div>
                                <div>
                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5">{t('users.table.presence')}</p>
                                    <div className="flex flex-col gap-0.5">
                                        <span className={`inline-flex items-center gap-1 font-bold text-[9px] uppercase tracking-widest ${user.isOnline ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-1 h-1 rounded-full ${user.isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                                            {user.isOnline ? t('users.presence.online') : t('users.presence.offline')}
                                        </span>
                                        {!user.isOnline && user.lastSeenAt && (
                                            <span className="text-[8px] text-gray-400 font-medium">
                                                {t('users.presence.last_seen', {
                                                    time: new Date(user.lastSeenAt).toLocaleString([], {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })
                                                })}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal */}
            {
                showModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setShowModal(false)} />
                        <div className="bg-white rounded-2xl w-full max-w-2xl py-6 px-1 relative z-10 shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                            <button
                                onClick={() => setShowModal(false)}
                                className="absolute top-4 right-4 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
                            >
                                <X size={18} />
                            </button>

                            <h2 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight mb-0.5 px-6 md:px-7">
                                {editingUser ? t('users.actions.edit') : t('users.actions.create')}
                            </h2>
                            <p className="text-gray-400 text-xs md:text-sm font-medium mb-4 md:mb-5 px-6 md:px-7">
                                {editingUser ? t('users.form.edit_subtitle', { name: editingUser.name }) : t('users.form.create_subtitle')}
                            </p>

                            {error && (
                                <div className="mb-4 md:mb-5 mx-6 md:mx-7 bg-red-50 text-red-600 p-3 rounded-xl text-xs md:text-sm font-medium border border-red-100 flex items-center gap-2">
                                    <AlertCircle size={14} className="md:w-4 md:h-4" />
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleFormSubmit} className="space-y-4 overflow-y-auto scrollbar-thin px-7" style={{ scrollbarWidth: 'thin', scrollbarColor: '#e5e7eb transparent' }} autoComplete="off">
                                {/* Decoy inputs to absorb browser autofill */}
                                <input style={{ display: 'none' }} type="text" name="fakeusernameremembered" />
                                <input style={{ display: 'none' }} type="password" name="fakepasswordremembered" />

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                                    <div>
                                        <label className="block text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5">{t('users.form.full_name')}</label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-3.5 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-gray-50/80 font-semibold text-xs md:text-sm text-gray-800"
                                            placeholder={t('users.form.placeholders.full_name')}
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            autoComplete="off"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5">{t('users.form.email_address')}</label>
                                        <input
                                            type="email"
                                            required
                                            className="w-full px-3.5 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-gray-50/80 font-semibold text-xs md:text-sm text-gray-800"
                                            placeholder={t('users.form.placeholders.email')}
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5">
                                        {editingUser ? t('users.form.password_edit') : t('users.form.password')}
                                    </label>
                                    <input
                                        type="password"
                                        name="new-password"
                                        autoComplete="new-password"
                                        required={!editingUser}
                                        minLength={6}
                                        className="w-full px-3.5 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-gray-50/80 font-semibold text-xs md:text-sm text-gray-800"
                                        placeholder=""
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                                    <div>
                                        <label className="block text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5">{t('users.form.account_role')}</label>
                                        <select
                                            className="w-full px-3.5 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-gray-50/80 font-semibold text-xs md:text-sm text-gray-800 appearance-none cursor-pointer"
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                        >
                                            <option value="confirmation">{t('users.roles.confirmation')}</option>
                                            <option value="gerant">{t('users.roles.gerant')}</option>
                                            <option value="admin">{t('users.roles.admin')}</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5">{t('users.form.status')}</label>
                                        <select
                                            className="w-full px-3.5 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-gray-50/80 font-semibold text-xs md:text-sm text-gray-800 appearance-none cursor-pointer"
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        >
                                            <option value="active">{t('users.status.active')}</option>
                                            <option value="disabled">{t('users.status.disabled')}</option>
                                        </select>
                                    </div>
                                </div>

                                {formData.role === 'confirmation' && (
                                    <>
                                        <ProductMultiSelect
                                            selectedProductNames={formData.allowedProducts}
                                            onChange={(names) => setFormData({ ...formData, allowedProducts: names })}
                                        />

                                        <div className="bg-indigo-50/30 p-4 md:p-5 rounded-lg md:rounded-xl border border-indigo-100/50 space-y-3 md:space-y-4">
                                            <h3 className="text-[10px] md:text-xs font-bold text-indigo-800 flex items-center gap-2">
                                                {t('users.form.payment_info')}
                                            </h3>
                                            <div className="grid grid-cols-1 gap-2.5 md:gap-3">
                                                <div>
                                                    <label className="block text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5">{t('users.form.payment_type')}</label>
                                                    <select
                                                        className="w-full px-3.5 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-white font-semibold text-xs md:text-sm text-gray-800 appearance-none cursor-pointer shadow-sm"
                                                        value={formData.paymentType}
                                                        onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                                                    >
                                                        <option value="FIXED_SALARY">{t('users.form.payment_types.fixed_salary')}</option>
                                                        <option value="SALARY_PLUS_COMMISSION">{t('users.form.payment_types.salary_plus_commission')}</option>
                                                        <option value="COMMISSION_ONLY">{t('users.form.payment_types.commission_only')}</option>
                                                    </select>
                                                </div>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 md:gap-3">
                                                    {(formData.paymentType === 'FIXED_SALARY' || formData.paymentType === 'SALARY_PLUS_COMMISSION') && (
                                                        <div>
                                                            <label className="block text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5">{t('users.form.monthly_salary')}</label>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3.5 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-white font-semibold text-xs md:text-sm text-gray-800 shadow-sm"
                                                                value={formData.monthlySalary}
                                                                onChange={(e) => setFormData({ ...formData, monthlySalary: Number(e.target.value) })}
                                                            />
                                                        </div>
                                                    )}

                                                    {(formData.paymentType === 'SALARY_PLUS_COMMISSION' || formData.paymentType === 'COMMISSION_ONLY') && (
                                                        <div>
                                                            <label className="block text-[9px] md:text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5">{t('users.form.commission')}</label>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3.5 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl border border-gray-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-white font-semibold text-xs md:text-sm text-gray-800 shadow-sm"
                                                                value={formData.commissionPerDeliveredOrder}
                                                                onChange={(e) => setFormData({ ...formData, commissionPerDeliveredOrder: Number(e.target.value) })}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="pt-2 md:pt-3 mt-auto">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full bg-gray-900 hover:bg-indigo-600 text-white font-bold py-2.5 md:py-3 rounded-lg md:rounded-xl transition-all duration-300 shadow-md shadow-gray-200 flex items-center justify-center gap-2 text-xs md:text-sm"
                                    >
                                        {isSubmitting ? (
                                            <Loader2 className="w-3.5 h-3.5 md:w-4 md:h-4 animate-spin" />
                                        ) : (
                                            editingUser ? t('users.actions.save') : t('users.actions.create_account')
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
 
            {/* Delete Confirmation Modal */}
            {userToDelete && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setUserToDelete(null)} />
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 relative z-10 shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="flex items-center gap-3 text-red-600 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                                <Trash2 size={20} />
                            </div>
                            <h3 className="text-lg font-bold">{t('users.delete_confirm.title')}</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-6">
                            {t('users.delete_confirm.message', { name: userToDelete.name })}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setUserToDelete(null)}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-100 font-bold text-gray-400 hover:bg-gray-50 transition-all text-xs"
                                disabled={isSubmitting}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteUser}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition-all shadow-md shadow-red-100 flex items-center justify-center gap-2 text-xs"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : t('common.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toasts */}
            <div className="fixed top-6 right-6 z-[110] flex flex-col gap-3">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl border animate-in slide-in-from-right-full duration-300 ${toast.type === 'success'
                            ? 'bg-white border-green-100 text-gray-900'
                            : 'bg-white border-red-100 text-gray-900'
                            }`}
                    >
                        {toast.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="font-semibold text-sm">{toast.message}</span>
                    </div>
                ))}
            </div>
        </div >
    );
};

export default Users;
